import { DateTime } from "luxon";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { AnimateAppearanceWrapper } from "../../../../components/animate-appearence/AnimateAppearanceWrapper";
import {
  Status,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import { ArrowLeft } from "../../../../components/icons";
import { OrderDetailsTableWithNotes } from "./components/OrderDetailsTableWithNotes";
import { InformationBlock } from "./components/InformationBlock";
import { OrderApiType, PopulatedOrderApiType } from "../../../../util/types";
import { useEffect, useState } from "react";
import api from "../../../../util/api";
import ReactJson from "react-json-view";
import { isNotFoundApiError } from "../../../../util/apiErrorMessage";
import PageNotFound from "../../../../components/page-not-found";

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const TopLine = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 20px;
  @media (max-width: 900px) {
    flex-direction: column;
    align-items: flex-end;
    gap: 10px;
  }
`;

const OrderID = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  @media (max-width: 900px) {
    margin-bottom: 20px;
  }
`;

const TypographyWithLine = styled(Typography)`
  line-height: 20px;
  padding-right: 12px;
  margin-right: 12px;
  border-right: 1px solid #adb5bd;
`;

const OrderInfo = styled(Container)``;

const ProductsInfoLine = styled.div`
  display: flex;
  align-items: center;
  @media (max-width: 900px) {
    flex-wrap: wrap;
    gap: 10px;
    justify-content: flex-end;
  }
`;

const BackArrow = styled(ArrowLeft)`
  margin-right: 5px;
  padding: 5px 5px 5px 0;
  :hover {
    cursor: pointer;
  }
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 5fr 2fr;
  gap: 24px;
  @media (max-width: 900px) {
    display: flex;
    flex-wrap: wrap;
  }
`;

const StyledStatus = styled(Status)`
  padding: 2px 10px 2px 10px;
`;

const RawDataContainer = styled.div`
  /* display: flex;
  flex-direction: column; */
  padding: 20px 30px 30px;
  @media (max-width: 767px) {
    padding: 14px;
  }
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 10px;
  height: max-content;
  @media (max-width: 900px) {
    padding: 20px 10px;
  }
`;

export const OrderDetails: React.FunctionComponent = () => {
  const { orderId } = useParams();

  const navigate = useNavigate();
  // const [searchParams] = useSearchParams();

  const [data, setData] = useState<OrderApiType>();
  const populatedData = data as unknown as PopulatedOrderApiType | undefined;
  const [notFound, setNotFound] = useState(false);
  const [fatalError, setFatalError] = useState<unknown>(null);

  useEffect(() => {
    const getOrderDetails = async (orderId: string) => {
      setNotFound(false);
      setFatalError(null);
      try {
        const responseOrder = await api.getOrderDetails(orderId);
        setData(responseOrder);
      } catch (e: unknown) {
        if (isNotFoundApiError(e)) {
          setNotFound(true);
          return;
        }
        setFatalError(e);
      }
    };
    getOrderDetails(orderId!);
  }, [orderId]);

  const handleBack = () => {
    navigate(-1);
  };

  // END Handlers

  if (fatalError) {
    throw fatalError;
  }

  if (notFound) {
    return <PageNotFound />;
  }

  return (
    <>
      {data ? (
        <AnimateAppearanceWrapper>
          <Container>
            <TopLine>
              <OrderInfo>
                <OrderID>
                  <BackArrow onClick={handleBack} />
                  <Typography variant={TypographyVariant.HEADER1}>
                    Order #{data.reference}
                  </Typography>
                </OrderID>
                <ProductsInfoLine>
                  <TypographyWithLine variant={TypographyVariant.HEADLINE}>
                    {DateTime.fromISO(String(data.createdAt)).toFormat(
                      "MMMM d, yyyy 'at' HH:mm"
                    )}
                  </TypographyWithLine>
                  <TypographyWithLine variant={TypographyVariant.HEADLINE}>
                    {data.products?.length}{" "}
                    {data.products?.length === 1 ? "item" : "items"}
                  </TypographyWithLine>
                  <TypographyWithLine variant={TypographyVariant.HEADLINE}>
                    Total{" "}
                    {new Intl.NumberFormat("en-GB", {
                      style: "currency",
                      currency: "GBP",
                    }).format(Number(data.totalAmount?.replaceAll(",", "")))}
                  </TypographyWithLine>

                  <StyledStatus
                    status={populatedData?.payment?.status ?? "Unfulfilled"}
                  />
                </ProductsInfoLine>
              </OrderInfo>
            </TopLine>
            <ContentWrapper>
              <OrderDetailsTableWithNotes
                data={data}
                // refreshOrderDetails={() => {
                //   orderId && getOrderDetails(orderId);
                // }}
              />
              <InformationBlock
                data={data}
                customer={populatedData?.customer}
              />

              <RawDataContainer>
                <Typography variant={TypographyVariant.HEADER2}>
                  Raw data
                </Typography>
                <ReactJson
                  src={data}
                  enableClipboard
                  collapsed
                  name={"order"}
                />
              </RawDataContainer>
            </ContentWrapper>
          </Container>
        </AnimateAppearanceWrapper>
      ) : null}
    </>
  );
};
